import { Filters } from "@shopify/polaris";
import React, { useCallback, useEffect, useMemo } from "react";

import { useQuery } from "@apollo/react-hooks";
import { get, isEmpty } from "lodash";
import { useAppContext } from "../../context";
import { GET_STORES } from "../../graphql/queries";
import { GET_AMAZON_IDEA_TYPE } from "../../graphql/queries/amazon-idea";
import { arrInvalid, reducerFn } from "../../helper";
import useTimeout from "../../hooks/useTimeout";
import { useTeamUserFilter } from "../../hooks/useUserTeamByRoles";
import { PLATFORMS, TEAM_ROLE } from "../../variable";
import { fitlerOptions } from "../channel-team/helper";
import { FilterHasSearchMVPolaris } from "../filters/FilterHasSearchMVPolaris";
import { FilterListRadioPolaris } from "../filters/FilterListRadioPolaris";
import { FilterCreatedTimePolaris } from "../product/FilterCreatedTimePolaris";
import { useAmazonIdeaCtx } from "./context";

const All = "All";
const Yes = "Yes";
const No = "No";

const BOOL_OPTIONS = [
    { value: All, label: All },
    { value: Yes, label: Yes },
    { value: No, label: No },
];

export default function AmazonIdeaFilter() {
    const [state, setState] = React.useReducer(reducerFn, {
        search: "",

        authorValue: [],
        authorLabel: "",
        authorSearch: "",

        pickerValue: [],
        pickerLabel: "",
        pickerSearch: "",

        typeValue: [],
        typeLabel: [],
        typeSearch: "",

        // storeTypeValue: [],
        // storeTypeLabel: [],
        // storeTypeSearch: "",

        personalizedValue: "",
        personalizedLabel: "",

        rangeValue: null,
        rangeLabel: null,

        storeValue: [],
        storeLabel: [],
        storeSearch: "",
    });

    const { store } = useAppContext();

    const { filter, setFilter } = useAmazonIdeaCtx();

    const {
        options: authorOptions,
        loading: authorLoading,
        error: authorError,
    } = useTeamUserFilter({
        value: [],
        roles: [
            TEAM_ROLE.ChannelIdea,
            TEAM_ROLE.ChannelSeller,
            TEAM_ROLE.ChannelLeader,
        ],
    });

    const {
        options: pickerOptions,
        loading: pickerLoading,
        error: pickerError,
    } = useTeamUserFilter({
        value: [],
        roles: [TEAM_ROLE.ChannelSupporter],
    });

    const {
        data: typeData,
        loading: typeLoading,
        error: typeError,
    } = useQuery(GET_AMAZON_IDEA_TYPE, {
        variables: {
            filter: {
                limit: 20,
                offset: 0,
                search: state.typeSearch,
            },
        },
    });

    // const {
    //     data: storeTypeData,
    //     loading: storeTypeLoading,
    //     error: storeTypeError,
    // } = useQuery(GET_STORE_TYPES, {
    //     variables: {
    //         filter: {
    //             limit: 20,
    //             offset: 0,
    //             search: state.storeTypeSearch,
    //         },
    //     },
    // });

    const {
        data: storeData,
        loading: storeLoading,
        error: storeError,
    } = useQuery(GET_STORES, {
        variables: {
            filter: {
                limit: 20,
                offset: 0,
                search: state.storeSearch,
                platform: PLATFORMS.Amazon,
            },
        },
    });

    const [delay] = useTimeout();

    const hasStoreContext = store?.id && store.id !== "all";

    useEffect(() => {
        if (!setFilter) return;
        delay(() => {
            setFilter({
                search: state.search ? state.search.trim() : state.search,
                authorIDs:
                    state.authorValue?.length > 0 ? state.authorValue : null,
                pickerIDs:
                    state.pickerValue?.length > 0 ? state.pickerValue : null,
                types: state.typeValue?.length > 0 ? state.typeValue : null,
                ...(hasStoreContext
                    ? {}
                    : {
                          storeIDs:
                              state.storeValue?.length > 0
                                  ? state.storeValue
                                  : null,
                      }),
                range: state.rangeValue === "All" ? null : state.rangeValue,
                isPersonalized:
                    state.personalizedValue === Yes
                        ? true
                        : state.personalizedValue === No
                        ? false
                        : null,
            });
        });
    }, [
        state.search,
        state.authorValue,
        state.pickerValue,
        state.typeValue,
        state.storeValue,
        state.personalizedValue,
        state.rangeValue,

        hasStoreContext,
    ]);

    const [setDelay] = useTimeout();
    const handleQueryChange = React.useCallback((search) => {
        setState({ search });

        setDelay(() => {
            setFilter({ search });
        });
    }, []);

    const handleQueryClear = React.useCallback(() => {
        setState({ search: "" });

        setDelay(() => {
            setFilter({ search: "" });
        });
    }, []);

    const handleStateChange = useCallback(
        (key) =>
            ({ value, label }) => {
                setState({ [`${key}Value`]: value, [`${key}Label`]: label });
            },
        [],
    );

    const handleStateSearch = useCallback(
        (key) => (search) => {
            setState({ [`${key}Search`]: search });
        },
        [],
    );

    const authorOptionsMemo = useMemo(
        () => fitlerOptions(authorOptions, state.authorSearch),
        [state.authorSearch, authorOptions],
    );

    const pickerOptionsMemo = useMemo(
        () => fitlerOptions(pickerOptions, state.pickerSearch),
        [state.pickerSearch, pickerOptions],
    );

    const typeOptions = useMemo(
        () => getTypeOptions(typeData),
        [state.typeSearch, typeData],
    );
    // const storeTypeOptions = useMemo(
    //     () => getStoreTypeOptions(storeTypeData),
    //     [state.storeTypeSearch, storeTypeData],
    // );

    const storeOptionsMemo = useMemo(
        () => getStoreOptions(storeData, state.storeSearch),
        [state.storeSearch, storeData],
    );

    const handleStateRemove = useCallback(
        (key, isSingle) => () =>
            setState({
                [`${key}Value`]: isSingle ? null : [],
                [`${key}Label`]: isSingle ? null : [],
            }),
        [],
    );

    const handleClearAll = React.useCallback(() => {
        handleQueryClear();

        for (let key of [
            "author",
            "picker",
            "type",
            "storeType",
            "personalized",
            "range",
        ]) {
            let isSingle = ("personalized", "range").includes(key);
            handleStateRemove(key, isSingle)();
        }
    }, [handleQueryClear, handleStateRemove]);

    const filters = [
        {
            key: "author",
            label: "Author",
            filter: (
                <FilterHasSearchMVPolaris
                    value={{
                        value: state.authorValue,
                        // search: state.authorSearch,
                    }}
                    options={authorOptionsMemo}
                    loading={authorLoading}
                    error={authorError}
                    onChange={handleStateChange("author")}
                    onChangeSearch={handleStateSearch("author")}
                />
            ),
        },
        {
            key: "picker",
            label: "Picker",
            filter: (
                <FilterHasSearchMVPolaris
                    value={{
                        value: state.pickerValue,
                        // search: state.pickerSearch,
                    }}
                    options={pickerOptionsMemo}
                    loading={pickerLoading}
                    error={pickerError}
                    onChange={handleStateChange("picker")}
                    onChangeSearch={handleStateSearch("picker")}
                />
            ),
        },
        {
            key: "type",
            label: "Type",
            filter: (
                <FilterHasSearchMVPolaris
                    value={{
                        value: state.typeValue,
                        // search: state.typeSearch,
                    }}
                    options={typeOptions}
                    loading={typeLoading}
                    error={typeError}
                    onChange={handleStateChange("type")}
                    onChangeSearch={handleStateSearch("type")}
                />
            ),
        },
        // {
        //     key: "storeType",
        //     label: "Store Type",
        //     filter: (
        //         <FilterHasSearchMVPolaris
        //             value={{
        //                 value: state.storeTypeValue,
        //             }}
        //             options={storeTypeOptions}
        //             loading={storeTypeLoading}
        //             error={storeTypeError}
        //             onChange={handleStateChange("storeType")}
        //             onChangeSearch={handleStateSearch("storeType")}
        //         />
        //     ),
        // },
        store?.id && store.id !== "all"
            ? null
            : {
                  key: "store",
                  label: "Store",
                  filter: (
                      <FilterHasSearchMVPolaris
                          value={{
                              value: state.storeValue,
                          }}
                          options={storeOptionsMemo}
                          loading={storeLoading}
                          error={storeError}
                          onChange={handleStateChange("store")}
                          onChangeSearch={handleStateSearch("store")}
                      />
                  ),
              },
        {
            key: "personalized",
            label: "Personalized",
            filter: (
                <FilterListRadioPolaris
                    value={state.personalizedValue}
                    options={BOOL_OPTIONS}
                    onChange={handleStateChange("personalized")}
                />
            ),
        },
        {
            key: "created-time",
            label: "Created Time",
            filter: (
                <FilterCreatedTimePolaris
                    filterTime={{
                        range: state.rangeValue,
                        rangeLabel: state.rangeLabel,
                    }}
                    onChangeRange={(value, label) => {
                        handleStateChange("range")({ value, label });
                    }}
                />
            ),
        },
    ].filter(Boolean);

    const appliedFilters = [];
    // if (!isEmpty(state.typeLabel)) {
    //     const key = "type";
    //     appliedFilters.push({
    //         key,
    //         label: disambiguateLabel(key, state.typeLabel),
    //         onRemove: handleStateRemove("type"),
    //     });
    // }

    if (!isEmpty(state.storeLabel)) {
        const key = "store";
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, state.storeLabel),
            onRemove: handleStateRemove("store"),
        });
    }

    if (state.storeTypeLabel && !arrInvalid(state.storeTypeLabel)) {
        const key = "storeType";
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, state.storeTypeLabel.join(", ")),
            onRemove: handleStateRemove("storeType"),
        });
    }

    if (state.authorLabel && !arrInvalid(state.authorLabel)) {
        const key = "author";
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, state.authorLabel.join(", ")),
            onRemove: handleStateRemove("author"),
        });
    }
    if (!isEmpty(state.pickerLabel)) {
        const key = "picker";
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, state.pickerLabel),
            onRemove: handleStateRemove("picker"),
        });
    }

    if (!isEmpty(state.personalizedLabel)) {
        const key = "personalized";
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, state.personalizedLabel),
            onRemove: handleStateRemove("personalized", true),
        });
    }

    if (!isEmpty(state.rangeLabel)) {
        const key = "created-time";
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, state.rangeLabel),
            onRemove: handleStateRemove("range", true),
        });
    }

    return (
        <Filters
            filters={filters}
            appliedFilters={appliedFilters}
            queryPlaceholder="Search Amazon Idea"
            queryValue={state.search}
            onClearAll={handleClearAll}
            onQueryChange={handleQueryChange}
            onQueryClear={handleQueryClear}
        />
    );
}

function getTypeOptions(data) {
    const nodes = get(data, "getAmazonIdeaType") || [];

    if (arrInvalid(nodes)) return [];

    return Array.from(new Set([...nodes])).map((value) => ({
        value,
        label: value,
    }));
}

function getStoreTypeOptions(data) {
    const nodes = get(data, "getStoreTypes.nodes") || [];

    if (arrInvalid(nodes)) return [];

    const res = [];
    for (let node of nodes) {
        if (!node || !node.id) continue;

        res.push({ value: node.id, label: node.label });
    }

    return res;
}

function getStoreOptions(data) {
    const nodes = get(data, "stores.nodes") || [];

    if (arrInvalid(nodes)) return [];

    const res = [];
    for (let node of nodes) {
        if (!node || !node.id) continue;

        res.push({ value: node.id, label: node.title });
    }

    return res;
}

function disambiguateLabel(key, value) {
    switch (key) {
        case "picker":
            return `Picker: ${value}`;
        case "storeType":
            return `Store Type: ${value}`;
        case "type":
            return `Type: ${value}`;
        case "author":
            return `Author: ${value}`;
        case "store":
            return `Store: ${value}`;
        case "personalized":
            return `Personalized: ${value}`;
        case "created-time":
            return `Created time: ${value}`;
        default:
            return value;
    }
}
